import { useEffect, useRef } from "react";

/**
 * Custom hook to synchronize a top horizontal scrollbar with a table scroll container.
 * Specifically prevents vertical scroll jitter, ping-pong events, and layout thrashing.
 *
 * @param {React.RefObject} scrollRef - Ref of the main table scroll container
 * @returns {React.RefObject} topScrollRef - Ref to be attached to the top scrollbar container
 */
export const useDoubleScroll = (scrollRef, deps = []) => {
  const topScrollRef = useRef(null);

  useEffect(() => {
    const content = scrollRef.current;
    const topScroll = topScrollRef.current;

    if (!content || !topScroll) return;

    let isSyncing = false;
    let lastContentScrollLeft = content.scrollLeft;
    let lastTopScrollLeft = topScroll.scrollLeft;
    let rafId = null;

    // Dynamically match top scroll width with table scroll width
    const updateWidth = () => {
      if (topScroll && topScroll.firstChild && content) {
        const targetWidth = `${Math.max(content.scrollWidth, content.offsetWidth)}px`;
        if (topScroll.firstChild.style.width !== targetWidth) {
          topScroll.firstChild.style.width = targetWidth;
        }
      }
    };

    // Run immediately and in next frame
    updateWidth();
    const timeoutId = setTimeout(updateWidth, 50);

    const resizeObserver = new ResizeObserver(() => {
      updateWidth();
    });

    resizeObserver.observe(content);
    const table = content.querySelector("table");
    if (table) {
      resizeObserver.observe(table);
    }

    // Sync content (table) -> topScroll
    const onContentScroll = () => {
      const currentScrollLeft = content.scrollLeft;
      // CRITICAL: If horizontal scroll position did not change, this is a VERTICAL scroll!
      // Return immediately so vertical scrolling remains smooth and never shakes/jitters!
      if (currentScrollLeft === lastContentScrollLeft) return;
      lastContentScrollLeft = currentScrollLeft;

      if (isSyncing) return;
      if (Math.abs(topScroll.scrollLeft - currentScrollLeft) < 1) return;

      isSyncing = true;
      topScroll.scrollLeft = currentScrollLeft;
      lastTopScrollLeft = currentScrollLeft;

      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        isSyncing = false;
      });
    };

    // Sync topScroll -> content (table)
    const onTopScroll = () => {
      const currentScrollLeft = topScroll.scrollLeft;
      if (currentScrollLeft === lastTopScrollLeft) return;
      lastTopScrollLeft = currentScrollLeft;

      if (isSyncing) return;
      if (Math.abs(content.scrollLeft - currentScrollLeft) < 1) return;

      isSyncing = true;
      content.scrollLeft = currentScrollLeft;
      lastContentScrollLeft = currentScrollLeft;

      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        isSyncing = false;
      });
    };

    content.addEventListener("scroll", onContentScroll, { passive: true });
    topScroll.addEventListener("scroll", onTopScroll, { passive: true });

    return () => {
      clearTimeout(timeoutId);
      content.removeEventListener("scroll", onContentScroll);
      topScroll.removeEventListener("scroll", onTopScroll);
      if (rafId) cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
    };
  }, [scrollRef, ...deps]);

  return topScrollRef;
};

export default useDoubleScroll;
